import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { PDFDocument, StandardFonts, rgb, PDFTextField, PDFCheckBox } from 'pdf-lib';
import { DentalCredentialsService } from '../../../services/dental-credentials.service';
import { DocumentCategory, DocumentSubCategory, DocumentSubCategoryName } from '../../../../../shared/constants/document.constants';
import { v4 as uuidv4 } from 'uuid';
import { LoaderService } from '../../../../../shared/services/loader.service';
import { ButtonComponent, TextInputComponent } from 'ddmo-storybook-lib';
import { SharedService } from '../../../../../shared/services/shared.service';
import { PDF_SCHEMAS } from '../../pdf-signing/pdf-schemas';
import { MatDialog } from '@angular/material/dialog';

const DEBUG = false; // toggle component logs on/off
const SIGNATURE_MAX_LENGTH = 90;
const LEGAL_NAME_PATTERN = /^[A-Za-z][A-Za-z'.-]*(?:\s+[A-Za-z][A-Za-z'.-]*)*$/;

const DEFAULT_SIGNATURE = {
    BASE_WIDTH: 200,
    BASE_HEIGHT: 60,
};

/** Supported signature kinds (typed text, uploaded/drawn image) */
type SignatureType = 'drawn' | 'typed' | 'uploaded';

/** Overlay model for a placed signature */
interface PlacedSignature {
    type: SignatureType;
    data: string; // dataURL for image, plain string for typed
    x: number; // left/top in page-local CSS px
    y: number;
    width: number; // current overlay width/height in CSS px
    height: number;
    baseWidth: number; // footprint at scale = 1
    baseHeight: number;
    isDragging: boolean;
    isResizing: boolean;
    isFixed: boolean;
    pageIndex?: number; // 0-based page index
    xPdf?: number; // optional: not needed when we compute from DOM
    yPdf?: number;
}

/** Cache for AcroForm metadata (field widgets) */
interface AcroCache {
    objects: Record<string, any[]>;
    names: string[];
}

type FieldValidation = {
    pattern: RegExp;
    label: string;
    message: string;
};

const FIELD_VALIDATIONS: Record<string, FieldValidation> = {
    'Date': {
        pattern: /^(0?[1-9]|1[0-2])[-/](0?[1-9]|[12][0-9]|3[01])[-/]\d{4}$/,
        label: 'Date',
        message: 'Date must be in MM/DD/YYYY format.'
    },
};

const CORP_PATCH_KEY_ALIASES: Record<string, string[]> = {
    dentistName: ['Dentist Name please print', 'Dentist Name (please print)', 'Dentist name please print', 'dhFormfield-6120490044', 'Dentist name'],
    dob: ['Date', 'Date of birth', 'DOB', 'dhFormfield-6120490091'],
    serviceStreetAddress: ['Professional Address', 'Primary service office street address', 'Street address', 'dhFormfield-6120490477'],
    serviceCity: ['City', 'dhFormfield-6120490483'],
    serviceState: ['State', 'dhFormfield-6120490490'],
    serviceZip: ['Zip Code', 'Zip code', 'dhFormfield-6120490502'],
    servicePhone: ['Phone', 'Phone #', 'dhFormfield-6120490585'],
    stateLicense: ['State License', 'MO state license #', 'MO state license', 'dhFormfield-6120490895'],
    npiType1: ['NPI Type 1', 'NPI type 1', 'dhFormfield-6120490971'],
    payeeName: ['Name of Corporation', 'Payment office name', 'Payee name', 'dhFormfield-6120490984'],
    payeeAddress: ['Professional Address _2', 'Professional Address_2', 'dhFormfield-6120492940'],
    payeeCity: ['City_2', 'dhFormfield-6120492966'],
    payeeState: ['State_2', 'dhFormfield-6120492981'],
    payeeZip: ['Zip Code_2', 'Zip code_2', 'dhFormfield-6120493380'],
    tin: ['Tax ID', 'SSN or tax ID', 'TIN', 'dhFormfield-6120493703'],
    currentDate: ['Date', 'dhFormfield-6120494277']
};

@Component({
    selector: 'app-corp-authority-form',
    imports: [CommonModule, FormsModule, PdfViewerModule, ButtonComponent, TextInputComponent],
    templateUrl: './corp-authority-form.component.html',
    styleUrl: './corp-authority-form.component.scss'
})
export class CorpAuthorityFormComponent implements OnDestroy {
    @Output() close = new EventEmitter<void>();
    @Output() done = new EventEmitter<any>();
    @Output() deleteDoc = new EventEmitter<any>();
    @Input() documentData: any;
    @Input() locData: any;
    @ViewChild('signatureCanvas', { static: true }) signatureCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('pdfContainer', { static: true }) pdfContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('signatureOverlay', { static: false }) signatureOverlay?: ElementRef<HTMLDivElement>;

    // Doc routing info
    documentTitle = 'Corp Authority Form';
    basePath: any = '/assets/pdf';
    uniformRequirements: string = `${this.basePath}/ddmo_uniform_requirements.pdf`;
    participatingAgreement: string = `${this.basePath}/participating_agreement.pdf`;
    w9Form: string = `${this.basePath}/w9_form.pdf`;

    // Signature inputs
    isDrawing = false;
    typedSignature = '';
    signatureFile: File | null = null;
    signatureFileUrl: string | null = null;
    signatureScale = 1;
    // PDF.js & forms state
    pdfDoc?: any; // PDFDocumentProxy
    fieldIndexById = new Map<string, { name: string; type: string }>();
    // Signature overlay model
    placedSignature: PlacedSignature | null = null;
    // Drawing internals
    private ctx: CanvasRenderingContext2D | null = null;
    private lastX = 0;
    private lastY = 0;
    // PDF render state
    private pdfReady = false;
    private validationListenersBound = false;
    private autoPatchedSomKeys = new Set<string>();
    private autoPatchedAcroNames = new Set<string>();
    private autoPatchedWidgetIds = new Set<string>();
    private autoLockObserver?: MutationObserver;
    // Drag helpers
    private dragOffset = { x: 0, y: 0 };
    // Cached Acro metadata
    private acroCache?: AcroCache;
    pdfSource: any;
    documentsMap: any;
    docData: any;
    fileName: any;
    baseFileName: any;
    PatchValues: Record<string, any> = {};
    private patchSeedValues: Record<string, any> = {};
    documentsArray: any = []
    params: any;
    showPdf: boolean = true;
    isPdfLoading: boolean = true;
    locations: any = [];
    private originalPdfBlob: Blob | null = null;
    readonly expectedSignerName = this.resolveExpectedProviderSignatureName();
    readonly isSignerNameAutoResolved = !!this.expectedSignerName;
    public inputForm = this.formBuilder.group({
        signText: [{ value: this.expectedSignerName, disabled: this.isSignerNameAutoResolved }, [Validators.required, Validators.minLength(2), Validators.maxLength(SIGNATURE_MAX_LENGTH), Validators.pattern(LEGAL_NAME_PATTERN)]]
    });
    isSignatureFixed: boolean = false;
    autoPlacementConfig = {
        alignment: 'bottom-right' as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom',
        marginX: 50,
        marginY: 50
    };
    sigData: any;
    impersonatedUser: any = localStorage.getItem("impersonatedUser") ?? ""
    customErrorMessages = {
        required: 'Please enter your full legal name.',
        minlength: 'Please enter a minimum of 2 characters.',
        maxlength: `Signature cannot exceed ${SIGNATURE_MAX_LENGTH} characters.`,
        pattern: 'Please enter first and last name using letters only.'
    };
    private pdfAlreadyFlattened = false;

    constructor(private router: Router, private route: ActivatedRoute,
        private dentalCredentialingService: DentalCredentialsService, private loader: LoaderService,
        private formBuilder: FormBuilder, private sharedService: SharedService, public dialog: MatDialog) {
    }

    ngOnInit() {
        this.documentsMap = this.documentData ? this.documentData : null;
        this.docData = this.documentsMap ? this.documentsMap : null;
        this.fileName = (this.docData && this.docData['originalFileName']?.split(".")[0]) || 'corporate_authority_form.pdf'
        const isSigned = this.docData?.isSigned ?? false;

        this.isSignatureFixed = isSigned;
        this.pdfAlreadyFlattened = isSigned;

        if (!this.docData) {
            this.setPatchValues();
        }

        this.baseFileName = 'corporate_authority_form.pdf';
        this.onViewDocument(this.fileName, true);
        let docs = localStorage.getItem('documents')
        this.documentsArray = docs ? JSON.parse(docs) : []
        const storedLocations = localStorage.getItem('locations');
        this.locations = storedLocations ? JSON.parse(storedLocations) : [];
    }

    onCancel() {
        this.close.emit();
    }

    onDone() {
        // Validate if needed
        this.close.emit();
    }
    
    onViewDocument(doc: any, isView: boolean = false): any {
        let filename = typeof doc == 'string' ? doc : doc.originalFileName;
        if (isView) {
            this.isPdfLoading = true;
        }
        this.dentalCredentialingService.viewAzureDocument(this.docData ? filename.split(".")[0] : filename).subscribe({
            next: (response: any) => {
                if (isView) {
                    this.originalPdfBlob = response.body;
                    this.pdfSource = window.URL.createObjectURL(response.body);
                }
                else {
                    const url = window.URL.createObjectURL(response.body);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = (this.docData?.isSigned ?
                        'Signed_' : '') + `${typeof doc == 'string' ? doc : doc.name}`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                }
            }, error: (err: any) => {
                if (isView) {
                    this.isPdfLoading = false;
                }
                this.sharedService.logError("APIFailed", "FileCorp-Authority-FormComponent - viewAzureDocument, Method:onViewDocument", this.router.url.split('?')[0], err).subscribe();
            }
        })
    }

    setPatchValues() {
        const credentials = JSON.parse(localStorage.getItem('credential')!);
        let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
        const hasLivePayeeSelection = !!(
            this.locData?.payeeAddressLine1 ||
            this.locData?.payeeLocation ||
            this.locData?.payeeCity ||
            this.locData?.payeeState ||
            this.locData?.payeeZip
        );
        let payeeDetails: any[] = [];
        let addressKey1 = 'payeeAddressLine1';
        let addressKey2 = 'payeeAddressLine2';
        if (hasLivePayeeSelection) {
            payeeDetails = [{
                payeeName: this.locData?.payeeName || '',
                payeeAddressLine1: this.locData?.payeeAddressLine1 || this.locData?.payeeLocation || '',
                payeeAddressLine2: this.locData?.payeeAddressLine2 || this.locData?.payeeAddress2 || '',
                payeeCity: this.locData?.payeeCity || '',
                payeeState: this.locData?.payeeState || '',
                payeeZip: this.locData?.payeeZip || ''
            }];
        } else {
            const TINDetails = JSON.parse(localStorage.getItem('TINDetails')!);
            payeeDetails = TINDetails?.filter((item: any) => item['taxId'] === this.locData.tin) || [];
            addressKey1 = 'payeeAddress1';
            addressKey2 = 'payeeAddress2';
        }
        if (!Object.entries(payeeDetails)?.length) {
            let searchedTinResponse = JSON.parse(localStorage.getItem('SearchTINResponse') || '[]');
            addressKey1 = 'payeeAddressLine1';
            addressKey2 = 'payeeAddressLine2';
            if (!searchedTinResponse) {
                const userDetails: any = JSON.parse(localStorage.getItem("loggedUserDetails")!);
                const payload = {
                    taxId: this.locData.tin,
                    pageSize: 200,
                    pageNumber: 1
                };

                this.sharedService.searchTIN(payload).subscribe({
                    next: (res: any) => {
                        const filteredRecords = res?.filter((item: any) => item.licenseState == userDetails.licenseState);
                        localStorage.setItem('SearchTINResponse', JSON.stringify(filteredRecords))
                        searchedTinResponse = filteredRecords
                        payeeDetails = searchedTinResponse?.filter((item: any) => item.taxId == this.locData.tin);
                    },
                    error: (err) => {
                        console.error(err);
                    },
                    complete: () => {
                        this.loader.hide();
                    }
                });
            } else {
                payeeDetails = searchedTinResponse?.filter((item: any) => item.taxId == this.locData.tin);
            }
        }
        if (!payeeDetails.length) {
            if (this.locData?.serviceLocationChecked) {
                payeeDetails = [{
                    payeeName: this.locData.name,
                    payeeAddressLine1: this.locData?.location,
                    payeeAddressLine2: "",
                    payeeCity: this.locData?.city,
                    payeeState: this.locData?.state,
                    payeeZip: this.locData?.zip
                }]
            } else {
                payeeDetails = [{}] // required to avoid undefined errors in PatchValues
            }
        }
        if (this.locData) {
            this.patchSeedValues = {
                dentistName: this.locData.dentistName,
                dob: this.formatUTCToCustom(new Date()),
                serviceStreetAddress: this.locData.location,
                serviceCity: this.locData.city,
                serviceState: this.locData.state,
                serviceZip: this.locData.zip,
                servicePhone: this.locData.voicePhone,
                stateLicense: (credentials && credentials[0] && credentials[0]['stateLicenseNumber']) || '',
                npiType1: credentialPayload['npi'] || '',
                payeeName: (payeeDetails && payeeDetails[0]?.payeeName) || '',
                payeeAddress: ((payeeDetails && payeeDetails[0][addressKey1]) || '') + ' ' + ((payeeDetails && payeeDetails[0][addressKey2]) || ''),
                payeeCity: (payeeDetails && payeeDetails[0]?.payeeCity) || '',
                payeeState: (payeeDetails && payeeDetails[0]?.payeeState) || '',
                payeeZip: (payeeDetails && payeeDetails[0]?.payeeZip) || '',
                tin: this.locData.tin,
                currentDate: this.formatUTCToCustom(new Date())
            };
            // Fallback until PDF field names are discovered.
            this.PatchValues = { ...this.patchSeedValues };
        }
    }

    async saveDocument(file: any = null) {
        const loginUserId = localStorage.getItem("loggedUserId");
        const sub_category = DocumentSubCategory.CorporateAuthorityForm;

        let date = new Date();

        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        const uniqueId = uuidv4();
        let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);

        this.params = {
            "credentialingEventId": localStorage.getItem('credentialingEventId'),
            "categoryId": DocumentCategory.Location,
            "subCategoryId": sub_category,
            "fileName": `${year}${month}${day}_${credentialPayload['npi']}_${uniqueId}`,
            "isSigned": this.isSignatureFixed || false,
            "userId": loginUserId || -1
        }
        file = await this.downloadSignedPdfWithSignature();
        return await this.dentalCredentialingService.saveDocument(file, this.params);
    }

    onDeleteDocument(doc: any) {
        const loginUserId = localStorage.getItem("loggedUserId");
        const credentialingEventId = localStorage.getItem('credentialingEventId');
        const requestParams = {
            "credentialingEventID": Number(credentialingEventId),
            "fileName": doc.originalFileName.split(".")[0],
            "modifiedUserID": loginUserId ? Number(loginUserId) : 0
        }
        this.loader.show();
        this.dentalCredentialingService.deleteDocument(requestParams).subscribe({
            next: (response: any) => {
                this.showPdf = false;
                this.documentsMap[this.documentTitle] = [];
                this.docData = null;
                this.isSignatureFixed = false;
                this.setPatchValues();
                this.documentsArray = this.documentsArray.filter((item: any) => item.documentId != doc.documentId)
                localStorage.setItem('documents', JSON.stringify(this.documentsArray))
                this.onViewDocument(this.baseFileName, true);
                if (this.locations.length != 0) {
                    const updatedLocations = this.locations.map((loc: any) =>
                        loc.dcaDocumentId == doc.documentId ? { ...loc, dcaDocumentId: null } : loc
                    );
                    localStorage.setItem('locations', JSON.stringify(updatedLocations))
                }
                setTimeout(() => {
                    this.showPdf = true;
                    this.loader.hide();
                }, 1000);
                this.deleteDoc.emit();

            }, error: (err: any) => {
                this.sharedService.logError("APIFailed", "FileCorp-Authority-FormComponent - deleteDocument, Method:onDeleteDocument", this.router.url.split('?')[0], err).subscribe();
            }
        })
    }

    /* ------------------------------------------------------------
     * Lifecycle
     * ------------------------------------------------------------
    */

    ngAfterViewInit() {
        if (this.signatureCanvas) {
            this.ctx = this.signatureCanvas.nativeElement.getContext('2d');
            if (this.ctx) {
                this.ctx.strokeStyle = '#000';
                this.ctx.lineWidth = 2;
                this.ctx.lineCap = 'round';
            }
        }
    }

    ngOnDestroy(): void {
        this.autoLockObserver?.disconnect();
        this.autoLockObserver = undefined;
    }

    /* ------------------------------------------------------------
     * Logging
     * ------------------------------------------------------------
    */

    private log(label: string, ...args: any[]) {
        if (DEBUG) {
            console.log(`[CorpAuthorityForm] ${label}`, ...args);
        }
    }

    private normalizeFieldName(name: string): string {
        return String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    private findBestFieldName(candidates: string[], availableNames: string[]): string | null {
        for (const candidate of candidates) {
            if (availableNames.includes(candidate)) return candidate;
        }

        const normalizedAvailable = availableNames.map((n) => ({
            raw: n,
            normalized: this.normalizeFieldName(n)
        }));

        for (const candidate of candidates) {
            const normCandidate = this.normalizeFieldName(candidate);
            const exactNormalized = normalizedAvailable.find((n) => n.normalized === normCandidate);
            if (exactNormalized) return exactNormalized.raw;
        }

        for (const candidate of candidates) {
            const normCandidate = this.normalizeFieldName(candidate);
            const contains = normalizedAvailable.find(
                (n) => n.normalized.includes(normCandidate)
            );
            if (contains) return contains.raw;
        }

        return null;
    }

    private async resolvePatchValuesFromPdfKeys(seedValues: Record<string, any>): Promise<Record<string, any>> {
        const cache = await this.ensureAcroCache();
        const availableNames = cache?.names || [];
        const resolved: Record<string, any> = {};
        const mappingRows: any[] = [];

        if (!availableNames.length) {
            this.log('No PDF field names discovered. Using seed keys as-is.');
            return { ...seedValues };
        }

        for (const [logicalKey, value] of Object.entries(seedValues || {})) {
            const aliases = CORP_PATCH_KEY_ALIASES[logicalKey] || [logicalKey];
            const resolvedKey = this.findBestFieldName(aliases, availableNames) || aliases[0];
            resolved[resolvedKey] = value;

            mappingRows.push({
                logicalKey,
                resolvedKey,
                aliasesTried: aliases.join(' | '),
                value
            });
        }

        if (DEBUG) {
            console.log('[CorpAuthorityForm] PDF field names:', availableNames);
            console.table(mappingRows);
        }

        return resolved;
    }

    private async logAvailablePdfFields() {
        if (!DEBUG || !this.pdfDoc?.getFieldObjects) return;

        try {
            const fieldObjects = await this.pdfDoc.getFieldObjects();
            const rows: any[] = [];
            Object.entries(fieldObjects || {}).forEach(([name, widgets]: any) => {
                (widgets || []).forEach((w: any) => {
                    rows.push({
                        fieldName: name,
                        widgetId: w?.id,
                        type: w?.type,
                        exportValue: w?.exportValues
                    });
                });
            });

            console.log('[CorpAuthorityForm] Discovered PDF field names:', Object.keys(fieldObjects || {}));
            if (rows.length) {
                console.table(rows);
            }
        } catch (error) {
            this.log('logAvailablePdfFields failed', error);
        }
    }

    /* ------------------------------------------------------------
     * PDF load hook (from ng2-pdf-viewer)
     * ------------------------------------------------------------
    */

    async onPdfLoaded(pdf: any) {
        this.pdfReady = true;
        this.pdfDoc = pdf;
        await this.indexFormFields();
        // await this.logAvailablePdfFields(); // knowing the pdf fields
        await this.patchAfterLoad();

        // If overlay existed, reparent
        this.reparentOverlayIfNeeded();

        setTimeout(() => {
            this.bindValidationListeners();
        }, 300);
        this.isPdfLoading = false;
    }

    private async flattenLoadedPdf() {
        if (!this.originalPdfBlob) return;

        const buffer = await this.originalPdfBlob.arrayBuffer();
        const pdfDoc = await PDFDocument.load(buffer);

        try {
            const form: any = pdfDoc.getForm();
            await this.normalizeFlattenTextFieldAppearance(pdfDoc);
            form.flatten({ updateFieldAppearances: false });
        } catch (e) { }

        const bytes = await (pdfDoc as any).save({ updateFieldAppearances: false });
        const blob = new Blob([bytes as any], { type: 'application/pdf' });
        this.pdfSource = URL.createObjectURL(blob);
    }

    private async patchAfterLoad() {
        if (!this.pdfReady || !this.pdfDoc) {
            return;
        }

        await this.indexFormFields();
        if (!this.docData) {
            this.PatchValues = await this.resolvePatchValuesFromPdfKeys(this.patchSeedValues);
            this.captureAutoPatchedKeys(this.PatchValues);
            await this.applyAcroPatch(this.PatchValues)
            await this.disableAutoPatchedFields();

            // Re-apply lock once more after viewer annotations finish rendering.
            setTimeout(() => {
                this.disableAutoPatchedFields();
            }, 500);
        }
    }

    private bindValidationListeners() {
        if (this.validationListenersBound || !this.pdfContainer?.nativeElement) {
            return;
        }

        const container = this.pdfContainer.nativeElement;

        // Prevent edits on auto-patched fields even if PDF.js re-renders DOM nodes.
        const blockLockedFieldEdit = (event: Event) => {
            const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
            if (!target) return;

            const name = target.getAttribute('name') || '';
            const annotationHost = (target as HTMLElement).closest('[data-annotation-id]') as HTMLElement | null;
            const annotationId = annotationHost?.getAttribute('data-annotation-id') || '';
            const isLockedByName = !!name && this.autoPatchedAcroNames.has(name);
            const isLockedByWidget = !!annotationId && this.autoPatchedWidgetIds.has(annotationId);
            const isLockedByClass = (target as HTMLElement).classList.contains('auto-patched-locked-field');
            if (!isLockedByName && !isLockedByWidget && !isLockedByClass) return;

            event.preventDefault();
            event.stopPropagation();
            target.blur();
            this.applyAutoPatchedDomLocks();
        };

        container.addEventListener('beforeinput', blockLockedFieldEdit, true);
        container.addEventListener('keydown', blockLockedFieldEdit, true);
        container.addEventListener('paste', blockLockedFieldEdit, true);

        this.validationListenersBound = true;
    }

    /* ============================================================
     * Signature input actions (typed / uploaded / drawn)
     * ============================================================
    */

    // Canvas signature
    startDrawing(event: MouseEvent) {
        this.isDrawing = true;
        const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();
        this.lastX = event.clientX - rect.left;
        this.lastY = event.clientY - rect.top;
    }

    draw(event: MouseEvent) {
        if (!this.isDrawing || !this.ctx) return;
        const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        this.ctx!.beginPath();
        this.ctx!.moveTo(this.lastX, this.lastY);
        this.ctx!.lineTo(x, y);
        this.ctx!.stroke();
        this.lastX = x;
        this.lastY = y;
    }

    stopDrawing() {
        this.isDrawing = false;
    }

    clearSignature() {
        if (this.ctx) {
            const c = this.signatureCanvas.nativeElement;
            this.ctx.clearRect(0, 0, c.width, c.height);
        }
    }

    useDrawnSignature() {
        const canvas = this.signatureCanvas.nativeElement;
        const dataUrl = canvas.toDataURL(); // png
        this.placeSignatureOnPdf('drawn', dataUrl);
    }

    async useTypedSignature() {
        if (!await this.guardRequiredFieldsOrAlert()) {
            return;
        }
        const signControl = this.inputForm.get('signText');
        if (!this.isSignerNameAutoResolved) {
            signControl?.markAsTouched();
            signControl?.updateValueAndValidity();
            if (signControl?.invalid) {
                return;
            }
        }

        const searchString = this.getResolvedSignatureName();
        if (!searchString) {
            return;
        }

        this.placedSignature = {
            type: 'typed',
            data: searchString, // Store raw text here to avoid base64 rendering in HTML
            x: 0,
            y: 0,
            width: DEFAULT_SIGNATURE.BASE_WIDTH,
            height: DEFAULT_SIGNATURE.BASE_HEIGHT,
            baseWidth: DEFAULT_SIGNATURE.BASE_WIDTH,
            baseHeight: DEFAULT_SIGNATURE.BASE_HEIGHT,
            isDragging: false,
            isResizing: false,
            isFixed: true,
            pageIndex: -1,
        };

        try {
            await this.patchSignatureToPdf(true);
            this.fixSignature();
        } catch (error) {
            this.sharedService.logError("APIFailed", "FileCorp-Authority-FormComponent - patchSignatureToPdf, Method:useTypedSignature", this.router.url.split('?')[0], error).subscribe();
            this.placedSignature.isFixed = false;
            this.placedSignature.pageIndex = 0;
            this.placeSignatureOnPdf('typed', searchString);
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            this.signatureFile = input.files[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                this.signatureFileUrl = e.target?.result as string;
                this.placeSignatureOnPdf('uploaded', this.signatureFileUrl!);
            };
            reader.readAsDataURL(this.signatureFile);
        }
    }

    /* ============================================================
     * Overlay placement / drag / scale / fix
     * ============================================================
    */

    placeSignatureOnPdf(type: SignatureType, data: string) {
        this.signatureScale = 1;
        this.placedSignature = {
            type,
            data,
            x: 100,
            y: 200,
            width: DEFAULT_SIGNATURE.BASE_WIDTH,
            height: DEFAULT_SIGNATURE.BASE_HEIGHT,
            baseWidth: DEFAULT_SIGNATURE.BASE_WIDTH,
            baseHeight: DEFAULT_SIGNATURE.BASE_HEIGHT,
            isDragging: false,
            isResizing: false,
            isFixed: false,
            pageIndex: 0,
        };

        if (!this.pdfReady || !this.pdfContainer?.nativeElement) {
            setTimeout(() => this.reparentOverlayIfNeeded(), 0);
            return;
        }
        this.attachOverlayToPage(0);
        this.syncOverlayDomSizeFromModel();
        this.fitTypedSignatureFontToBox();
    }

    onScaleChange(event: Event) {
        const input = event.target as HTMLInputElement;
        this.signatureScale = parseFloat(input.value);
        if (!this.placedSignature) return;
        this.placedSignature.width = this.placedSignature.baseWidth * this.signatureScale;
        this.placedSignature.height = this.placedSignature.baseHeight * this.signatureScale;
        this.syncOverlayDomSizeFromModel();
        this.fitTypedSignatureFontToBox();
    }

    onSignatureMouseDown(event: MouseEvent) {
        if (!this.placedSignature || this.placedSignature.isFixed || !this.signatureOverlay) return;
        const rect = this.signatureOverlay.nativeElement.getBoundingClientRect();
        this.dragOffset.x = event.clientX - rect.left;
        this.dragOffset.y = event.clientY - rect.top;
        this.placedSignature.isDragging = true;
        event.preventDefault();
        event.stopPropagation();
    }

    onPdfMouseMove(event: MouseEvent) {
        if (!this.placedSignature || !this.placedSignature.isDragging || !this.signatureOverlay) return;

        const overlay = this.signatureOverlay.nativeElement;
        const pageEl = this.getPageElementAtPoint(event.clientX, event.clientY);
        if (!pageEl) return;

        const newPageIndex = this.getPageIndex(pageEl);
        if (overlay.parentElement !== pageEl) {
            pageEl.appendChild(overlay);
            this.placedSignature.pageIndex = newPageIndex;
        }

        const { xPx, yPx } = this.clientToPagePixels(event, pageEl, this.dragOffset);
        this.placedSignature.x = xPx;
        this.placedSignature.y = yPx;

        overlay.style.left = `${xPx}px`;
        overlay.style.top = `${yPx}px`;
    }

    onPdfMouseUp() {
        if (this.placedSignature) {
            this.placedSignature.isDragging = false;
        }
    }

    fixSignature() {
        if (!this.placedSignature) return;
        this.placedSignature.isFixed = true;
        this.signatureOverlay?.nativeElement.classList.add('fixed');
    }

    cancelPlacement() {
        this.placedSignature = null;
    }

    /* ============================================================
     * Forms (AcroForm) - reading & patching
     * ============================================================
    */

    async indexFormFields() {
        if (!this.pdfDoc?.getFieldObjects) return;
        const fieldObjects = await this.pdfDoc.getFieldObjects();
        if (fieldObjects && Object.keys(fieldObjects).length > 0) {
            Object.entries(fieldObjects)?.forEach(([name, arr]: any) => {
                arr.forEach((f: any) => {
                    if (f?.id) this.fieldIndexById.set(String(f.id), { name, type: f.type });
                });
            });
        }
    }

    /** SOM path "topmostSubform[0]...f1_1[0]" -> probable Acro field name "f1_1" */
    private async somToAcroName(som: string): Promise<string> {
        const m = som.match(/([^.[]+)\[\d+\]$/);
        return m ? m[1] : som; // fallback: raw tail
    }

    private async ensureAcroCache() {
        if (!this.pdfDoc?.getFieldObjects) return null;
        if (!this.acroCache) {
            const objects = await this.pdfDoc.getFieldObjects();
            this.acroCache = { objects, names: Object.keys(objects) };
        }
        return this.acroCache;
    }

    private async findWidgetsByName(candidate: string): Promise<any[] | null> {
        if (!this.acroCache) return null;
        const { objects, names } = this.acroCache;
        if (objects[candidate]) return objects[candidate];
        const ends = names.find((n) => n.endsWith(candidate));
        if (ends) return objects[ends];
        const contains = names.find((n) => n.includes(candidate));
        if (contains) return objects[contains];
        return null;
    }

    /** Apply Acro patch to annotationStorage (no download yet) */
    private async applyAcroPatch(somValues: Record<string, any>) {
        const cache = await this.ensureAcroCache();
        if (!cache || !this.pdfDoc?.annotationStorage) return;

        const storage = this.pdfDoc.annotationStorage;

        let patched = 0;
        for (const [somKey, somVal] of Object.entries(somValues)) {
            const name = await this.somToAcroName(somKey);
            const widgets = await this.findWidgetsByName(name);
            if (!widgets?.length) {
                continue;
            }

            for (const w of widgets) {
                if (w.type === 'text' || w.type === 'combobox' || w.type === 'textarea') {
                    storage.setValue(w.id, { value: String(somVal ?? '') });
                    patched++;
                } else if (w.type === 'checkbox') {
                    const onValue = w.exportValues || 'On';
                    storage.setValue(w.id, { value: !!somVal ? onValue : 'Off' });
                    patched++;
                } else if (w.type === 'radio') {
                    if (!!somVal) {
                        const onValue = w.exportValues || 'On';
                        storage.setValue(w.id, { value: onValue });
                        patched++;
                    }
                }
            }
        }
    }

    private captureAutoPatchedKeys(values: Record<string, any>) {
        this.autoPatchedSomKeys.clear();
        this.autoPatchedAcroNames.clear();
        this.autoPatchedWidgetIds.clear();

        for (const [somKey, value] of Object.entries(values || {})) {
            if (this.hasAutoPatchValue(value)) {
                this.autoPatchedSomKeys.add(somKey);
                const acroName = somKey.match(/([^.[]+)\[\d+\]$/)?.[1] ?? somKey;
                this.autoPatchedAcroNames.add(acroName);
            }
        }
    }

    private hasAutoPatchValue(value: any): boolean {
        return value !== undefined && value !== null;
    }

    private async disableAutoPatchedFields() {
        if (!this.autoPatchedSomKeys.size) return;

        const cache = await this.ensureAcroCache();
        if (!cache || !this.pdfDoc?.annotationStorage) return;

        const storage = this.pdfDoc.annotationStorage;

        for (const somKey of this.autoPatchedSomKeys) {
            const acroName = await this.somToAcroName(somKey);
            const widgets = await this.findWidgetsByName(acroName);

            for (const w of widgets || []) {
                this.autoPatchedWidgetIds.add(String(w.id));
                storage.setValue(w.id, {
                    readOnly: true,
                    disabled: true,
                });
            }

            const byName = document.querySelectorAll(
                `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`
            ) as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

            byName.forEach((el) => {
                el.setAttribute('readonly', 'true');
                el.setAttribute('disabled', 'true');
                (el as HTMLElement).style.pointerEvents = 'none';
                (el as HTMLElement).classList.add('auto-patched-locked-field');
            });

            for (const widget of widgets || []) {
                const widgetId = String(widget.id);
                const widgetSelector = `[data-annotation-id="${widgetId}"]`;
                const byWidget = document.querySelectorAll(widgetSelector) as NodeListOf<HTMLElement>;

                byWidget.forEach((host) => {
                    host.style.pointerEvents = 'none';
                    host.classList.add('auto-patched-locked-field');

                    const nested = host.querySelectorAll('input, textarea, select') as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
                    nested.forEach((el) => {
                        el.setAttribute('readonly', 'true');
                        el.setAttribute('disabled', 'true');
                        (el as HTMLElement).style.pointerEvents = 'none';
                        (el as HTMLElement).classList.add('auto-patched-locked-field');
                    });
                });
            }
        }

        this.applyAutoPatchedDomLocks();
        this.bindAutoLockObserver();
    }

    private applyAutoPatchedDomLocks() {
        if (!this.autoPatchedAcroNames.size && !this.autoPatchedWidgetIds.size) return;

        const container = this.pdfContainer?.nativeElement ?? document;

        for (const acroName of this.autoPatchedAcroNames) {
            const elements = container.querySelectorAll(
                `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`
            ) as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

            elements.forEach((el) => {
                el.setAttribute('readonly', 'true');
                el.setAttribute('disabled', 'true');
                (el as HTMLElement).style.pointerEvents = 'none';
                (el as HTMLElement).style.userSelect = 'none';
                (el as HTMLElement).classList.add('auto-patched-locked-field');
                if ((el as HTMLInputElement).value !== undefined) {
                    (el as HTMLInputElement).defaultValue = (el as HTMLInputElement).value;
                }
            });
        }

        for (const widgetId of this.autoPatchedWidgetIds) {
            const hosts = container.querySelectorAll(`[data-annotation-id="${widgetId}"]`) as NodeListOf<HTMLElement>;
            hosts.forEach((host) => {
                host.style.pointerEvents = 'none';
                host.style.userSelect = 'none';
                host.classList.add('auto-patched-locked-field');

                const nested = host.querySelectorAll('input, textarea, select') as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
                nested.forEach((el) => {
                    el.setAttribute('readonly', 'true');
                    el.setAttribute('disabled', 'true');
                    (el as HTMLElement).style.pointerEvents = 'none';
                    (el as HTMLElement).classList.add('auto-patched-locked-field');
                });
            });
        }
    }

    private bindAutoLockObserver() {
        const container = this.pdfContainer?.nativeElement;
        if (!container || this.autoLockObserver) return;

        this.autoLockObserver = new MutationObserver(() => {
            this.applyAutoPatchedDomLocks();
        });

        this.autoLockObserver.observe(container, {
            childList: true,
            subtree: true,
        });
    }

    /** Patch and download (fields only) */
    async patchAcroAndDownload(somValues: Record<string, any>, fileName = 'form-patched.pdf') {
        if (!this.pdfDoc) return;
        await this.applyAcroPatch(somValues);
        const bytes: Uint8Array = await this.pdfDoc.saveDocument();
        this.downloadBytes(bytes, fileName);
    }

    async downloadSignedPdfWithSignature() {

        if (!this.pdfDoc) {
            alert('PDF not loaded yet');
            return;
        }
        if (!this.isSignatureFixed) {
            alert('Please place and fix the signature first');
            return;
        }

        // If signature was already embedded (typed auto flow), upload the current
        // rendered PDF directly instead of regenerating from stale pre-sign state.
        if (!this.placedSignature && this.pdfSource && typeof this.pdfSource === 'string' && this.pdfSource.startsWith('blob:')) {
            const response = await fetch(this.pdfSource);
            const signedBytes = await response.arrayBuffer();
            const signedBlob = new Blob([signedBytes], { type: 'application/pdf' });
            const signedFileName = `${this.documentTitle || 'signed'}-${Date.now()}.pdf`;
            return new File([signedBlob], signedFileName, { type: 'application/pdf' });
        }

        // Ensure overlay reflects any latest scale/typed font before measuring
        if (this.placedSignature?.pageIndex !== -1) {
            this.syncOverlayDomSizeFromModel();
            this.fitTypedSignatureFontToBox();
        }
        // 1) Patch fields
        return await this.patchSignatureToPdf(false, true);
    }

    private async patchSignatureToPdf(isAutoPatch = false, returnFile = false) {
        if (!this.placedSignature && !returnFile) return;

        // 1. Get current PDF data - ALWAYS start from original to prevent cumulative artifacts
        let pdfData: ArrayBuffer | Uint8Array;
        if (this.pdfDoc?.saveDocument) {
            // Use the current viewer document state so all typed/patched form values are preserved.
            const bytes: Uint8Array = await this.pdfDoc.saveDocument();
            pdfData = bytes;
        } else if (this.originalPdfBlob) {
            pdfData = await this.originalPdfBlob.arrayBuffer();
        } else if (this.pdfSource && typeof this.pdfSource === 'string' && this.pdfSource.startsWith('blob:')) {
            const response = await fetch(this.pdfSource);
            pdfData = await response.arrayBuffer();
        } else {
            // Fallback
            const bytes: Uint8Array = await this.pdfDoc.saveDocument();
            pdfData = bytes;
        }

        // 2. Load into pdf-lib
        const pdfDoc = await PDFDocument.load(pdfData);

        // Keep current field appearance as rendered by PDF.js. Re-setting PDFTextField values
        // here can regenerate appearances and make text look larger after signature placement.
        const unifiedValues = await this.getUnifiedFormValues();

        // Field values are preserved from PDF.js annotationStorage via saveDocument().
        // Do not draw schema text again here; it causes overlap after flatten.

        let pageIndex = this.placedSignature?.pageIndex ?? -1;
        // Handle last page auto-placement
        if (pageIndex === -1) {
            pageIndex = pdfDoc.getPageCount() - 1;
        }

        let page = pdfDoc.getPage(pageIndex);
        let { width: pdfWidth, height: pdfHeight } = page.getSize();

        let x, y, drawWidth, drawHeight;

        if (isAutoPatch) {
            // Check if schema exists for this document
            const schema = PDF_SCHEMAS['corporateAuthorityForm'];
            const dateConfig = schema?.find(f => f.key === 'date');
            const sigConfig = schema?.find(f => f.key === 'signature');

            if (sigConfig && this.placedSignature && this.placedSignature?.pageIndex === -1) {
                // FIXED: Schema-based placement
                pageIndex = sigConfig.ui.page - 1;
                drawWidth = sigConfig.ui.width;
                drawHeight = sigConfig.ui.height;
                x = sigConfig.ui.x;

                // Re-resolve page and size when schema moves placement to another page.
                page = pdfDoc.getPage(pageIndex);
                ({ width: pdfWidth, height: pdfHeight } = page.getSize());
                y = pdfHeight - sigConfig.ui.y - drawHeight;
                
            } else {
                // FALLBACK: Initialize variables before they are used in calculations
                drawWidth = this.placedSignature?.width || DEFAULT_SIGNATURE.BASE_WIDTH;
                drawHeight = this.placedSignature?.height || DEFAULT_SIGNATURE.BASE_HEIGHT;

                if (this.placedSignature && this.placedSignature?.pageIndex === -1) {
                    const { alignment, marginX, marginY } = this.autoPlacementConfig;

                    switch (alignment) {
                        case 'bottom-right':
                            x = pdfWidth - drawWidth - marginX;
                            y = marginY;
                            break;
                        case 'bottom-left':
                            x = marginX;
                            y = marginY;
                            break;
                        case 'top-right':
                            x = pdfWidth - drawWidth - marginX;
                            y = pdfHeight - drawHeight - marginY;
                            break;
                        case 'top-left':
                            x = marginX;
                            y = pdfHeight - drawHeight - marginY;
                            break;
                        default:
                            x = pdfWidth - drawWidth - marginX;
                            y = marginY;
                    }
                } else {
                    // Manual placement was triggered with autoPatch=true (e.g. drawn signature)
                    x = this.placedSignature?.x || 0;
                    y = pdfHeight - (this.placedSignature?.y || 0) - (this.placedSignature?.height || DEFAULT_SIGNATURE.BASE_HEIGHT);
                }
            }

            this.sigData = this.placedSignature?.data || this.sigData || "";
            let typedSignatureContentWidth = 0;
            let typedSignatureContentHeight = 0;
            if (this.placedSignature?.type === 'typed') {
                const signatureUiConfig: any = PDF_SCHEMAS['corporateAuthorityForm']?.find(f => f.key === 'signature');
                if (!signatureUiConfig) {
                    return;
                }

                const maxWidth = Math.max(1, signatureUiConfig.ui.width);
                const maxHeight = Math.max(1, signatureUiConfig.ui.height);
                const paddingX = 10;
                const paddingY = 8;
                const scale = 2;
                const canvas = document.createElement('canvas');
                canvas.width = Math.floor(maxWidth * scale);
                canvas.height = Math.floor(maxHeight * scale);

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return;
                }

                let text = this.getResolvedSignatureName();
                if (text.startsWith('data:image')) {
                    text = '';
                }

                const minFont = 10 * scale;
                let fontSize = 26 * scale;

                if (text.length <= 10) fontSize = 20 * scale;
                else if (text.length <= 20) fontSize = 17 * scale;
                else if (text.length <= 25) fontSize = 15 * scale;
                else if (text.length <= 50) fontSize = 14 * scale;
                else if (text.length <= 60) fontSize = 10 * scale;
                else if (text.length <= 75) fontSize = 9 * scale;
                else fontSize = 6.5 * scale;

                const fontFamily = `"Brush Script MT","Segoe Script","Lucida Handwriting",cursive`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
                ctx.fillStyle = '#000';

                const setFont = (size: number) => {
                    ctx.font = `${size}px ${fontFamily}`;
                };

                const measure = (value: string) => {
                    const metrics = ctx.measureText(value);
                    const ascent = metrics.actualBoundingBoxAscent ?? 0;
                    const descent = metrics.actualBoundingBoxDescent ?? Math.max(0, metrics.fontBoundingBoxDescent ?? 0);
                    let textHeight = ascent + descent;
                    if (textHeight === 0) {
                        textHeight = fontSize * 1.2;
                    }

                    return { width: metrics.width, height: textHeight, ascent };
                };

                setFont(fontSize);
                let { width: measuredWidth, height: measuredHeight } = measure(text);
                const boxWidth = canvas.width - paddingX * 2 * scale;
                const boxHeight = canvas.height - paddingY * 2 * scale;

                while ((measuredWidth > boxWidth || measuredHeight > boxHeight) && fontSize > minFont) {
                    fontSize = Math.max(minFont, fontSize - scale);
                    setFont(fontSize);
                    const measured = measure(text);
                    measuredWidth = measured.width;
                    measuredHeight = measured.height;
                }

                const finalMetrics = measure(text);
                typedSignatureContentWidth = Math.min(
                    maxWidth,
                    Math.ceil(finalMetrics.width / scale) + (paddingX * 2)
                );
                typedSignatureContentHeight = Math.min(
                    maxHeight,
                    Math.ceil(finalMetrics.height / scale) + (paddingY * 2)
                );

                const tightCanvas = document.createElement('canvas');
                tightCanvas.width = Math.max(1, Math.ceil(typedSignatureContentWidth * scale));
                tightCanvas.height = Math.max(1, Math.ceil(typedSignatureContentHeight * scale));

                const tightCtx = tightCanvas.getContext('2d');
                if (!tightCtx) {
                    return;
                }

                tightCtx.clearRect(0, 0, tightCanvas.width, tightCanvas.height);
                tightCtx.textAlign = 'left';
                tightCtx.textBaseline = 'alphabetic';
                tightCtx.fillStyle = '#000';
                tightCtx.font = `${fontSize}px ${fontFamily}`;

                const startX = paddingX * scale;
                const baselineY = ((tightCanvas.height - finalMetrics.height) / 2) + finalMetrics.ascent;
                tightCtx.fillText(text, startX, baselineY);
                this.sigData = tightCanvas.toDataURL('image/png');
            }
            const signatureImage = await pdfDoc.embedPng(this.sigData);

            // Use a slightly smaller inner band for typed signatures so glyphs do not
            // overlap the label/line around the signature field.
            let renderX = x;
            let renderY = y;
            let renderW = drawWidth;
            let renderH = drawHeight;

            if (this.placedSignature?.type === 'typed') {
                const innerPaddingX = 6;
                const contentWidth = typedSignatureContentWidth || Math.max(1, drawWidth - (innerPaddingX * 2));
                const contentHeight = typedSignatureContentHeight || Math.min(drawHeight, 34);
                renderX = x + innerPaddingX + 14;
                renderW = Math.min(Math.max(1, drawWidth - (innerPaddingX * 2)), contentWidth);
                renderH = Math.min(drawHeight, contentHeight);
                renderY = y + Math.max(0, (drawHeight - renderH) / 2) - 5;
            }

            // 6. Draw on page
            page.drawImage(signatureImage, {
                x: renderX,
                y: renderY,
                width: renderW,
                height: renderH
            });

            if (dateConfig) {
                const datePageIndex = Math.max(0, (dateConfig.ui.page || 1) - 1);
                const datePage = pdfDoc.getPage(datePageIndex);
                const { height: datePageHeight } = datePage.getSize();
                const signatureDate = this.formatUTCToCustom(new Date());

                datePage.drawText(signatureDate, {
                    x: dateConfig.ui.x + 13,
                    y: datePageHeight - dateConfig.ui.y - dateConfig.ui.height - 14,
                    size: 11,
                    color: rgb(0, 0, 0),
                });
            }
        }

        try {
            const form: any = pdfDoc.getForm();
            await this.normalizeFlattenTextFieldAppearance(pdfDoc, unifiedValues);
            form.flatten({ updateFieldAppearances: false });
        } catch (e) {
        }

        // 7. Save
        const finalBytes = await (pdfDoc as any).save({ updateFieldAppearances: false });
        const pdfBlob = new Blob([finalBytes as any], { type: 'application/pdf' });
        const fileName = `${this.documentTitle || 'signed'}-${Date.now()}.pdf`;
        if (returnFile) {
            return new File([pdfBlob], fileName, { type: 'application/pdf' });
        }

        // Update viewer
        this.pdfSource = URL.createObjectURL(pdfBlob);
        this.placedSignature = null;
        this.isSignatureFixed = true;
        return null;
    }

    /**
     * Ensure text field appearances are rendered with consistent font size (11pt)
     * and positioned with a clean gap right above the underline (matching signature date style),
     * then flatten form fields cleanly using native pdf-lib flattening.
     */
    private async normalizeFlattenTextFieldAppearance(pdfDoc: PDFDocument, fallbackValues: Record<string, any> = {}) {
        try {
            const form = pdfDoc.getForm();
            const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const targetFontSize = 11;

            for (const field of form.getFields()) {
                if (!(field instanceof PDFTextField)) {
                    continue;
                }

                const fieldName = field.getName();
                const somName = (await this.somToAcroName(fieldName)) || fieldName;

                // Resolve text from current field first, then fallback sources.
                let text = field.getText?.();
                if (text === undefined || text === null || String(text).trim() === '') {
                    text = fallbackValues[fieldName] ??
                        fallbackValues[somName] ??
                        this.PatchValues[fieldName] ??
                        this.PatchValues[somName] ??
                        '';
                }

                if (!text) {
                    const normField = this.normalizeFieldName(fieldName);
                    for (const [logicalKey, aliases] of Object.entries(CORP_PATCH_KEY_ALIASES)) {
                        const matches = aliases.some(alias =>
                            this.normalizeFieldName(alias) === normField ||
                            normField.includes(this.normalizeFieldName(alias))
                        );
                        if (matches) {
                            const seedVal = this.patchSeedValues?.[logicalKey] ?? this.PatchValues?.[logicalKey] ?? fallbackValues?.[logicalKey];
                            if (seedVal !== undefined && seedVal !== null && String(seedVal).trim() !== '') {
                                text = String(seedVal).trim();
                                break;
                            }
                        }
                    }
                }

                const strVal = String(text ?? '');
                let fontSize = targetFontSize;

                const acroField: any = (field as any).acroField;
                const widgets: any[] = acroField?.getWidgets?.() || [];

                for (const widget of widgets) {
                    try {
                        const rect = widget.getRectangle?.();
                        if (rect && rect.width > 0 && rect.height > 0) {
                            // If text is long, proportionally adjust font size so it fits inside the widget width
                            if (strVal) {
                                const textWidth = helvetica.widthOfTextAtSize(strVal, fontSize);
                                const availableWidth = Math.max(10, rect.width - 4);
                                if (textWidth > availableWidth) {
                                    fontSize = Math.max(7.5, (availableWidth / textWidth) * targetFontSize);
                                }
                            }

                            // For single-line text fields (height <= 35), adjust widget bounding box
                            // so vertical centering naturally places text baseline close to the underline (gap matching sign date)
                            if (rect.height <= 35) {
                                const bottomGap = 1.5; // gap in points above the underline
                                const targetBoxHeight = Math.min(rect.height, fontSize + 3);
                                widget.setRectangle({
                                    x: rect.x,
                                    y: rect.y + bottomGap - 2.5,
                                    width: rect.width,
                                    height: targetBoxHeight
                                });
                            }
                        }
                    } catch {
                        // Keep original rect if widget manipulation fails
                    }
                }

                field.setFontSize(fontSize);

                const textFieldAny = field as any;
                if (typeof textFieldAny.setAlignment === 'function') {
                    textFieldAny.setAlignment(0); // left align
                }

                field.setText(strVal);
                textFieldAny.updateAppearances(helvetica);
            }
        } catch (error) {
            this.log('normalizeFlattenTextFieldAppearance failed', error);
        }
    }

    /* ============================================================
     * DOM / Overlay utilities
     * ============================================================
    */

    private attachOverlayToPage(pageIndex: number) {
        if (!this.signatureOverlay || !this.placedSignature) return;
        const pageEl = this.getPageElByIndex(pageIndex);
        if (!pageEl) {
            requestAnimationFrame(() => this.attachOverlayToPage(pageIndex));
            return;
        }
        pageEl.appendChild(this.signatureOverlay.nativeElement);
        this.signatureOverlay.nativeElement.style.left = `${this.placedSignature.x}px`;
        this.signatureOverlay.nativeElement.style.top = `${this.placedSignature.y}px`;
        this.syncOverlayDomSizeFromModel();
        this.fitTypedSignatureFontToBox();
        this.placedSignature.pageIndex = pageIndex;
    }
    
    private reparentOverlayIfNeeded() {
        if (!this.placedSignature || !this.signatureOverlay) return;
        const idx = this.placedSignature.pageIndex ?? 0;
        this.attachOverlayToPage(idx);
    }

    private getPageElementAtPoint(clientX: number, clientY: number): HTMLElement | null {
        const el = document.elementFromPoint(clientX, clientY);
        return el ? (el as HTMLElement).closest('.page') as HTMLElement : null;
    }

    private getPageIndex(pageEl: HTMLElement): number {
        const numAttr = pageEl.getAttribute('data-page-number');
        return numAttr ? parseInt(numAttr, 10) - 1 : 0;
    }

    private getPageElByIndex(pageIndex: number): HTMLElement | null {
        if (!this.pdfContainer || !this.pdfContainer.nativeElement) return null;
        return this.pdfContainer.nativeElement.querySelector(
            `.page[data-page-number="${pageIndex + 1}"]`
        );
    }

    private clientToPagePixels(
        evt: MouseEvent,
        pageEl: HTMLElement,
        dragOffset?: { x: number; y: number }
    ) {
        const rect = pageEl.getBoundingClientRect();
        const xClient = evt.clientX - rect.left;
        const yClient = evt.clientY - rect.top;
        return {
            xPx: dragOffset ? xClient - dragOffset.x : xClient,
            yPx: dragOffset ? yClient - dragOffset.y : yClient,
        };
    }

    /** Ensure overlay DOM width/height match the model */
    private syncOverlayDomSizeFromModel() {
        if (!this.signatureOverlay?.nativeElement || !this.placedSignature) return;
        const el = this.signatureOverlay.nativeElement;
        el.style.width = `${this.placedSignature.width}px`;
        el.style.height = `${this.placedSignature.height}px`;
    }

    /** Fit typed signature font-size to overlay box (width & height) */
    private fitTypedSignatureFontToBox() {
        if (!this.placedSignature || this.placedSignature.type !== 'typed') return;
        const typedEl = this.signatureOverlay?.nativeElement.querySelector(
            '.typed-signature'
        ) as HTMLElement | null;
        if (!typedEl) return;

        const targetWidth = this.placedSignature.width;
        const targetHeight = this.placedSignature.height;
        const text = this.placedSignature.data || '';

        const maxSizeByHeight = Math.max(8, targetHeight);
        let low = 8,
            high = maxSizeByHeight,
            best = low;

        for (let i = 0; i < 12; i++) {
            const mid = (low + high) / 2;
            const w = this.measureTypedTextWidthAtSize(text, mid, typedEl);
            if (w <= targetWidth) {
                best = mid;
                low = mid;
            } else {
                high = mid;
            }
        }

        const finalSize = Math.floor(Math.min(best, maxSizeByHeight));
        typedEl.style.fontSize = `${finalSize}px`;
        typedEl.style.lineHeight = '1';
        typedEl.style.whiteSpace = 'nowrap';
        typedEl.style.display = 'flex';
        typedEl.style.width = '100%';
        typedEl.style.height = '100%';
        typedEl.style.alignItems = 'center';
        typedEl.style.justifyContent = 'flex-start';
        typedEl.style.paddingLeft = '8px';
    }

    /** Width of text at font-size using DOM font (offscreen canvas) */
    private measureTypedTextWidthAtSize(text: string, fontSizePx: number, elem: HTMLElement): number {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return text.length * fontSizePx * 0.5;

        const cs = getComputedStyle(elem);
        const fontStyle = cs.fontStyle || 'normal';
        const fontWeight = cs.fontWeight || 'normal';
        const fontFamily = cs.fontFamily || 'BrushScript';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;
        const m = ctx.measureText(text || '');
        return m.width;
    }

    private getResolvedSignatureName(): string {
        if (this.isSignerNameAutoResolved) {
            return this.expectedSignerName.trim();
        }

        return (this.inputForm.get('signText')?.value || '').toString().trim();
    }

    private resolveExpectedProviderSignatureName(): string {
        const demographics = JSON.parse(localStorage.getItem('demographics') || '{}');
        const loggedUserDetails = JSON.parse(localStorage.getItem('loggedUserDetails') || '{}');
        const demographicLastName = demographics?.lastName || '';
        const demographicFullName = `${demographics?.firstName || ''} ${demographicLastName}`.trim();

        if (demographicFullName) {
            return demographicFullName.slice(0, SIGNATURE_MAX_LENGTH);
        }

        return `${loggedUserDetails?.firstName || ''} ${loggedUserDetails?.lastName || ''}`
            .trim()
            .slice(0, SIGNATURE_MAX_LENGTH);
    }

    /* ============================================================
     * Canvas rasterization for typed signature (IMAGE APPROACH)
     * ============================================================
    */

    /**
     * Render the typed signature element to a PNG data URL, using its computed CSS styles.
     * This ensures the final PDF matches the on-screen look exactly.
     * 
     * @param typedEl The .typed-signature element
     * @param targetWidth CSS px width of the signature box
     * @param targetHeight CSS px height of the signature box
     * @param pxToPtScale pageW / pageRect.width - used to bump resolution for PDF
    */
    private async renderTypedSignatureToPng(
        typedEl: HTMLElement,
        targetWidth: number,
        targetHeight: number,
        pxToPtScale: number
    ): Promise<string> {
        const text = typedEl.textContent || '';
        const cs = getComputedStyle(typedEl);

        // Build canvas and choose a resolution scale factor:
        // devicePixelRatio for crispness, plus pxToPtScale to match PDF scale.
        const dpr = Math.max(1, Math.floor((window.devicePixelRatio || 1) * 1.5));
        const scale = Math.max(2, Math.min(8, Math.ceil(dpr * pxToPtScale))); // clamp between 2x and 8x

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(targetWidth * scale));
        canvas.height = Math.max(1, Math.floor(targetHeight * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        // Render using CSS coordinate system scaled up
        ctx.scale(scale, scale);
        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.imageSmoothingEnabled = true;

        // Compose CSS-like font for Canvas
        const fontSizePx = parseFloat(cs.fontSize || '16');
        const fontWeight = cs.fontWeight || 'normal';
        const fontStyle = cs.fontStyle || 'normal';
        const fontFamily = cs.fontFamily || 'BrushScript';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;

        ctx.fillStyle = cs.color || '#000';
        ctx.textBaseline = 'middle';

        // Left align typed signature rendering inside the signature box.
        const letterSpacingRaw = cs.letterSpacing || 'normal';
        const letterSpacing =
            letterSpacingRaw === 'normal' ? 0 : parseFloat(letterSpacingRaw) || 0;

        // Measure width with letter-spacing taken into account
        const baseMetrics = ctx.measureText(text);
        const baseWidth = baseMetrics.width;
        const extraSpacing = letterSpacing * Math.max(0, text.length - 1);
        const totalWidth = baseWidth + extraSpacing;

        // Left align with small inner padding.
        const xStart = 8;
        const yMid = targetHeight / 2;

        // Draw each char to honor letter-spacing
        let xCursor = xStart;
        for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            // measure each char to advance; this keeps better spacing in cursive fonts
            const mw = ctx.measureText(ch).width;
            ctx.fillText(ch, xCursor, yMid);
            xCursor += mw + letterSpacing;
        }

        return canvas.toDataURL('image/png');
    }

    /* ============================================================
     * General utilities
     * ============================================================
    */

    private downloadBytes(bytes: Uint8Array, fileName: string) {
        const blob = new Blob([bytes as any], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    uploadSignedPdf() {
        if (!this.isSignatureFixed) {
            alert('Please place and fix the signature first');
            return;
        }
        // Implement back-end upload if needed
        this.saveDocument().then(res => {
            let docDetails = {
                "documentId": res.documentId,
                "credentialingEventId": Number(this.params?.credentialingEventId || "0"),
                "categoryId": this.params?.categoryId,
                "category": DocumentCategory.Location,
                "subCategoryId": DocumentSubCategory.CorporateAuthorityForm,
                "subcategory": DocumentSubCategoryName[DocumentSubCategory.CorporateAuthorityForm],
                "fileName": this.params?.fileName,
                "isSigned": this.params?.isSigned
            }
            const editLocationId = localStorage.getItem('editLocationId');
            if (editLocationId) {
                let locations = JSON.parse(localStorage.getItem('locations') || '[]');
                locations = locations.map((loc: any) => {
                    if (loc.locationId == editLocationId || loc.esLocationId == editLocationId) {
                        return { ...loc, dcaDocumentId: res.documentId, isCorpAuthResignRequired: false }
                    }
                    return loc;
                });
                localStorage.setItem('locations', JSON.stringify(locations));
            }
            this.documentsArray = [...this.documentsArray, docDetails]
            localStorage.setItem('documents', JSON.stringify(this.documentsArray));
            this.done.emit(res);
        });
    }

    private async guardRequiredFieldsOrAlert(): Promise<any> {
        try {
            await this.clearHighlights();
            // Pattern validation
            const unifiedValues = await this.getUnifiedFormValues();
            const patternResult = await this.validatePatternFields(unifiedValues);
            console.log('Pattern validation result:', patternResult);
            if (!patternResult.valid) {
                await this.highlightMissingFields(patternResult.missingKeys);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return false;
            }

            return true;
        } catch (error: any) {
            throw Error(error?.message);
        }
    }

    private async clearHighlights() {
        try {
            const highlighted = document.querySelectorAll('.missing-field-highlight');
            highlighted.forEach(el => el.classList.remove('missing-field-highlight'));
        } catch (error: any) {
            throw Error(error?.message)
        }
    }

    private async highlightMissingFields(missingSomKeys: string[], scrollToFirst: boolean = true) {
        try {
            let firstElementScrolled = false;

            for (const somKey of missingSomKeys) {
                const acroName = await this.somToAcroName(somKey);
                const widgets = await this.findWidgetsByName(acroName);
                let found = false;

                for (const w of widgets || []) {
                    // Strategy 1: Find by Name
                    const explicitSelector = `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`;
                    let elements: NodeListOf<Element> | HTMLElement[] = document.querySelectorAll(explicitSelector);

                    // If you have DOM mapping, add a CSS class here
                    if (elements.length > 0) {
                        found = true;
                    } else {
                        const widgets = await this.findWidgetsByName(acroName);
                        if (widgets && widgets.length > 0) {
                            const selectors = widgets.flatMap(w => [
                                `[data-annotation-id="${w.id}"]`,
                                `[data-annotation-id="${w.id}"] input`,
                                `[data-annotation-id="${w.id}"] textarea`,
                                `[data-annotation-id="${w.id}"] select`,
                                `[id="${w.id}"]`
                            ]);
                            const combinedSelector = selectors.join(', ');
                            try {
                                elements = document.querySelectorAll(combinedSelector);
                                if (elements.length > 0) {
                                    found = true;
                                }
                            } catch (error) {
                                console.error('Invalid selector generated', combinedSelector, error);
                            }
                        }
                    }

                    if (elements.length > 0) {
                        elements.forEach((el) => {
                            const element = el as HTMLElement;
                            element.classList.add('missing-field-highlight');

                            if (!firstElementScrolled) {
                                if (scrollToFirst) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                                firstElementScrolled = true;
                            }
                        });
                    } else {
                        this.log('Could not find DOM element for field:', acroName);
                    }
                }
            }
        } catch (error: any) {
            throw Error(error?.message);
        }
    }

    private async validatePatternFields(values: Record<string, any>) {
        const errors: string[] = [];
        const missingKeys: string[] = [];
        for (const [somKey, cfg] of Object.entries(FIELD_VALIDATIONS)) {
            const acroName: any = await this.somToAcroName(somKey);
            const val =
                values[somKey] ??
                values[acroName];

            if (val && !cfg.pattern.test(String(val).trim())) {
                errors.push(`${cfg.label} has invalid format`);
                missingKeys.push(somKey);
            }
        }

        return {
            valid: errors.length === 0,
            errors,
            missingKeys
        };
    }

    async goBack(signed: boolean = false) {
        this.router.navigate(['/credentialing/sign-documents'], {
            queryParams: signed ? { signed: 'true' } : {}
        });
    }

    getFormValuesFromViewer(): Record<string, any> {
        const result: Record<string, any> = {};
        if (!this.pdfDoc) return result;

        const storageAll =
            this.pdfDoc.annotationStorage?.getAll?.() ??
            (window as any).PDFViewerApplication?.pdfDocument?.annotationStorage?.getAll?.() ??
            {};

        for (const [id, payload] of Object.entries(storageAll)) {
            const idx = this.fieldIndexById.get(id);
            if (!idx) continue;
            const value =
                (payload as any).value ??
                (payload as any).textContent ??
                (payload as any).checked ??
                null;
            result[idx.name] = value;
        }
        return result;
    }

    formatUTCToCustom(date: any) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${month}/${day}/${year}`;
    }

    restrictNumeric(event: KeyboardEvent): void {
        this.sharedService.preventNumeric(event);
    }

    private async getUnifiedFormValues(): Promise<Record<string, any>> {
        const result: Record<string, any> = {};

        // 1. Start with PATCH_VALUES
        Object.assign(result, this.PatchValues);

        // 2. Merge annotationStorage
        const storageAll =
            this.pdfDoc?.annotationStorage?.getAll?.() ?? {};

        for (const [id, payload] of Object.entries(storageAll)) {
            const idx = this.fieldIndexById.get(id);
            if (!idx) continue;

            const val =
                (payload as any)?.value ??
                (payload as any)?.textContent ??
                (payload as any)?.checked ??
                payload;

            // IMPORTANT: Do NOT filter out 'Off' here - let checkbox logic handle it
            if (val !== undefined && val !== null) {
                result[idx.name] = val;
            }
        }

        // 3. Fallback to DOM for ALL indexed fields (Exhaustive Capture)
        const allKnownAcroNames = Array.from(new Set(Array.from(this.fieldIndexById.values()).map(v => v.name)));

        for (const acroName of allKnownAcroNames) {
            // Finding by acro name in DOM
            const input = document.querySelector(
                `input[name="${acroName}"], textarea[name="${acroName}"], select[name="${acroName}"]`
            ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;

            if (!input) continue;

            if (input instanceof HTMLInputElement && (input.type === 'checkbox' || input.type === 'radio')) {
                result[acroName] = input.checked;
            } else if (input.value?.trim()) {
                result[acroName] = input.value.trim();
            }
        }
        
        return result;
    }
}
